<template>
  <a-modal
    title="Apply Holiday Pay"
    :visible="visible"
    width="480px"
    :destroy-on-close="true"
    data-testid="shifts-holiday-pay-modal"
    @cancel="closeModal"
  >
    <a-form-model
      layout="horizontal"
      :model="form"
      :rules="rules"
      ref="formRef"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Date" prop="date">
        <a-date-picker v-model="form.date" placeholder="Holiday Date" class="w-full" :format="visualDateFormat" />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Start Time" prop="time">
        <a-time-picker
          :value="timeValue"
          :allow-clear="false"
          :default-open-value="defaultTimeValue"
          format="hh:mm A"
          :use12Hours="true"
          class="w-full"
          @change="handleTimeChange"
        />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Associates">
        <a-radio-group class="w-full" v-model="form.union_status" button-style="solid">
          <a-radio-button class="col-4 p-0 text-center" :value="undefined"> All </a-radio-button>
          <a-radio-button class="col-4 p-0 text-center" :value="true"> Union </a-radio-button>
          <a-radio-button class="col-4 p-0 text-center" :value="false"> Non-Union </a-radio-button>
        </a-radio-group>
      </a-form-model-item>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Cancel</a-button>
        <a-button type="primary" :loading="loading" @click="submit">Apply</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import CustomSelect from '@/components/ui/custom-select.vue'
import { Button, DatePicker, FormModel, InputNumber, notification, Radio, TimePicker } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import {
  formatHolidayDateForPayload,
  getScheduledStartForWorkDay,
  getWorkDayStartTime,
  isScheduledStartInWorkDay,
  normalizePayCodeTime,
  timePickerValue
} from '../../utils/pay-code-time'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')

const initialForm = {
  date: undefined,
  time: undefined,
  union_status: undefined
}

const initialRules = {
  date: [
    {
      required: true,
      message: 'Date can not be empty',
      trigger: 'change'
    }
  ],
  time: [
    {
      required: true,
      message: 'Start time can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'a-time-picker': TimePicker,
    'a-input-number': InputNumber,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'custom-select': CustomSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialValues: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      form: { ...initialForm },
      rules: {
        ...initialRules,
        time: [
          {
            validator: this.validateTime,
            trigger: 'change'
          }
        ]
      },

      loading: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.ensureDefaultTime()
        }

        if (newVal && (this.initialValues.start || this.initialValues.end)) {
          this.form.date = moment(this.initialValues.start ?? this.initialValues.end)
            .utc(true)
            .tz(this.$store.state.applicationState.timeZone)
            .startOf('day')
        }
      },
      immediate: true
    }
  },
  computed: {
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    paymentSettings() {
      return this.$store.state.applicationState.currentProject?.payment_settings
    },
    timeZone() {
      return this.$store.state.applicationState.timeZone
    },
    workDayStart() {
      return getWorkDayStartTime(this.paymentSettings)
    },
    defaultTimeValue() {
      return timePickerValue(this.workDayStart)
    },
    timeValue() {
      return timePickerValue(this.form.time, this.workDayStart)
    }
  },
  methods: {
    ...shiftActions(['postHolidayPay']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    ensureDefaultTime() {
      if (!this.form.time) {
        this.form.time = this.workDayStart
      }
    },

    handleTimeChange(time) {
      this.form.time = normalizePayCodeTime(time, this.workDayStart)
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('time')
      })
    },

    validateTime(rule, value, callback) {
      if (!value) {
        callback(new Error('Start time can not be empty'))
        return
      }

      if (!this.form.date) {
        callback()
        return
      }

      const scheduledStart = getScheduledStartForWorkDay(this.form.date, value, this.workDayStart, this.timeZone)

      if (!isScheduledStartInWorkDay(scheduledStart, this.form.date, this.workDayStart, this.timeZone)) {
        callback(new Error('Start time must be within the selected Work Day.'))
        return
      }

      callback()
    },

    convertToServerFormat(date) {
      return formatHolidayDateForPayload(date, this.workDayStart, this.timeZone)
    },

    closeModal() {
      this.$emit('update:visible', false)
    },

    async submit() {
      try {
        const valid = await new Promise(resolve => {
          this.$refs.formRef.validate(resolve)
        })

        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.loading = true

        await this.postHolidayPay({
          filters: {
            union_status: this.form.union_status
          },
          date: this.convertToServerFormat(this.form.date),
          time: normalizePayCodeTime(this.form.time, this.workDayStart)
        })

        this.closeModal()
        this.$emit('add')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style>
.ant-input-number {
  width: 100% !important;
}
</style>
